import { Workout, ExerciseBlock } from '@/types/workout';
import { generateWorkout, GeneratorParams } from './workoutGenerator';
import { getAlternatives, getExerciseById, ExerciseTemplate } from '@/data/exerciseLibrary';

/**
 * Stubbed AI service - generates workouts from parameters
 */
export async function generateWorkoutFromParams(
  duration: number,
  focus?: 'mobility' | 'strength' | 'core' | 'balance' | 'flexibility' | 'mix',
  intensity?: 'gentle' | 'moderate' | 'challenging'
): Promise<Workout> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));
  
  const params: GeneratorParams = { duration, focus, intensity };
  return generateWorkout(params);
}

/**
 * Get exactly 3 swap alternatives for an exercise
 * Preserves workout intent, timing, and constraints
 */
export async function getSwapAlternatives(
  exerciseId: string,
  workout: Workout
): Promise<ExerciseBlock[]> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 300));
  
  const currentExercise = workout.exercises.find(ex => ex.exercise_id === exerciseId);
  if (!currentExercise) return [];
  
  const alternatives = getAlternatives(exerciseId);
  
  // Filter alternatives to match workout focus/intensity if possible
  const filtered = alternatives.filter(alt => {
    if (workout.focus && workout.focus !== 'mix') {
      return alt.focus.includes(workout.focus);
    }
    return true;
  });
  
  // Take up to 3 alternatives
  const selected = (filtered.length > 0 ? filtered : alternatives).slice(0, 3);
  
  // Convert to ExerciseBlock format, preserving current exercise settings
  return selected.map((template, index) => {
    const newBlock: ExerciseBlock = {
      ...currentExercise,
      id: `swap-${Date.now()}-${index}`,
      exercise_id: template.id,
      name: template.name,
      instruction: template.instruction,
    };
    
    // Preserve mode and adjust target based on template
    if (currentExercise.target.mode === 'time') {
      newBlock.target = {
        mode: 'time',
        duration_seconds: template.defaultDuration,
      };
    } else {
      newBlock.target = {
        mode: 'reps',
        sets: currentExercise.target.sets || template.defaultSets || 3,
        reps: currentExercise.target.reps || template.defaultReps || 10,
        weight: currentExercise.target.weight,
        rpe: currentExercise.target.rpe,
      };
    }
    
    return newBlock;
  });
}

/**
 * Adjust warmup and cooldown durations for a workout
 */
export async function adjustWarmupCooldown(
  workout: Workout,
  warmupSeconds?: number,
  cooldownSeconds?: number
): Promise<Workout> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 200));
  
  return {
    ...workout,
    warmup_duration_seconds: warmupSeconds,
    cooldown_duration_seconds: cooldownSeconds,
  };
}

